import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

const fmtMonth = (m) => {
  const d = new Date(m + "-01T00:00:00");
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};
const gbp = (n) => `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;

export default function CostsPage() {
  const { theme } = useTheme();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics/costs").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const chartData = data.monthly.map((m) => ({ ...m, label: fmtMonth(m.month) }));
  const totals = data.by_vehicle.reduce(
    (a, v) => ({ services: a.services + v.services, tyres: a.tyres + v.tyres, fines: a.fines + v.fines, fuel: a.fuel + (v.fuel || 0) }),
    { services: 0, tyres: 0, fines: 0, fuel: 0 }
  );
  const grand = totals.services + totals.tyres + totals.fines + totals.fuel;

  return (
    <div className="p-8" data-testid="costs-page">
      <div className="mb-8">
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">FLEET COSTS</h1>
        <p className="text-muted-foreground text-sm mt-1">Monthly spend on services, tyres, fuel and PCN fines — last 12 months</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {[
          ["Total 12 months", grand, "text-foreground", "costs-kpi-total"],
          ["Services", totals.services, "text-foreground/80", "costs-kpi-services"],
          ["Tyres", totals.tyres, "text-amber-600 dark:text-[#FFD60A]", "costs-kpi-tyres"],
          ["Fuel", totals.fuel, "text-[#0A84FF]", "costs-kpi-fuel"],
          ["Fines", totals.fines, "text-[#FF3B30]", "costs-kpi-fines"],
        ].map(([label, val, cls, tid]) => (
          <div key={label} data-testid={tid} className="border border-border bg-card p-5">
            <div className="uppercase text-[10px] tracking-[0.25em] font-bold text-muted-foreground mb-3">{label}</div>
            <div className={`font-heading font-black text-3xl tracking-tighter ${cls}`}>{gbp(val)}</div>
          </div>
        ))}
      </div>

      <div className="border border-border bg-card p-5 mb-8" data-testid="costs-chart">
        <div className="uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground mb-4">Monthly spend</div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"} vertical={false} />
              <XAxis dataKey="label" stroke="#8E8E93" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.15)" }} />
              <YAxis stroke="#8E8E93" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => `£${v}`} />
              <Tooltip
                formatter={(v, name) => [gbp(v), name]}
                contentStyle={{ background: theme === "dark" ? "#141414" : "#ffffff", border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)", borderRadius: 0, color: theme === "dark" ? "#fff" : "#111" }}
                cursor={{ fill: theme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" }}
              />
              <Legend wrapperStyle={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.1em" }} />
              <Bar dataKey="services" name="Services" stackId="a" fill="#8E8E93" />
              <Bar dataKey="tyres" name="Tyres" stackId="a" fill="#FFD60A" />
              <Bar dataKey="fuel" name="Fuel" stackId="a" fill="#0A84FF" />
              <Bar dataKey="fines" name="Fines" stackId="a" fill="#FF3B30" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="text-[10px] text-muted-foreground/70 mt-2 uppercase tracking-widest">Tyre costs = service records mentioning "tyre" in the description</div>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <div className="px-5 py-4 border-b border-border uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground">Cost per vehicle (most expensive first)</div>
        <table className="w-full text-sm" data-testid="costs-by-vehicle-table">
          <thead>
            <tr className="text-left border-b border-border">
              {["Reg", "Services", "Tyres", "Fuel", "Fines", "Total"].map((h) => (
                <th key={h} className="px-5 py-3 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.by_vehicle.map((v) => (
              <tr key={v.vehicle_id} data-testid={`cost-row-${v.registration}`} className="border-b border-border/60 hover:bg-muted/60">
                <td className="px-5 py-3 font-mono font-bold">
                  {v.registration === "REMOVED" ? <span className="text-muted-foreground/70">REMOVED</span> : <Link to={`/vehicles/${v.vehicle_id}`} className="hover:text-[#FF3B30]">{v.registration}</Link>}
                </td>
                <td className="px-5 py-3 text-foreground/80">{gbp(v.services)}</td>
                <td className="px-5 py-3 text-amber-600 dark:text-[#FFD60A]">{gbp(v.tyres)}</td>
                <td className="px-5 py-3 text-[#0A84FF]">{gbp(v.fuel || 0)}</td>
                <td className="px-5 py-3 text-[#FF3B30]">{gbp(v.fines)}</td>
                <td className="px-5 py-3 font-heading font-black tracking-tighter text-base">{gbp(v.total)}</td>
              </tr>
            ))}
            {data.by_vehicle.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-12 text-center text-muted-foreground/70">No costs recorded yet — add service records and fines to see spend.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
